import * as AssetDocumentsActions from './asset-document.actions';

import {createEntityAdapter, EntityAdapter} from "@ngrx/entity";
import {createReducer, on} from "@ngrx/store";
import dayjs from 'dayjs';
import {StoredComponentHelperService} from '@common/helpers/stored-component.helper.service';
import {AssetDocument, AssetDocumentSearchParams} from '@accounting/_common/models/asset-document.models';
import {EntityStateModel} from '@common/models/entity-state.models';
import {SetAllInitialStates} from '@common/store/actions/core.actions';

export const assetDocumentsFeatureKey = 'assetDocuments';

export function sortByDate(a: AssetDocument, b: AssetDocument) {
    if (a.date !== b.date) {
        return StoredComponentHelperService.sortArrayComparer(a, b, 'date');
    }

    return (a.id || '').localeCompare(b.id || '');
}

export const adapter: EntityAdapter<AssetDocument> = createEntityAdapter<AssetDocument>({
    sortComparer: sortByDate
});

export type AssetDocumentsState = EntityStateModel<AssetDocument, AssetDocumentSearchParams>;

export const initialState: AssetDocumentsState = adapter.getInitialState({
    listLoading: 'listLoading',
    pagination: {},
    itemsLoading: []
});

export const assetDocumentsReducer = createReducer(
    initialState,

    on(AssetDocumentsActions.CheckState, (state, action) => {
        if (action.payload.modalView) {
            return adapter.removeAll({
                ...state,
                savedFilter: !action.payload.close && state.filter ? state.filter : undefined,
                pagination: initialState.pagination,
                filter: action.payload.close ? state.savedFilter : undefined
            });
        } else {
            if (action.payload.close) {
                return adapter.setAll(action.payload.items, {
                    ...state,
                    pagination: {...state.pagination, number: 0},
                    componentOpened: false
                });
            }

            return {...state, componentOpened: true};
        }
    }),

    on(AssetDocumentsActions.LoadList, (state, action) => ({
        ...state,
        listLoading: StoredComponentHelperService.updateListLoading(action.payload, state.ids)
    })),

    on(AssetDocumentsActions.LoadListSuccess, (state, action) => {
        return action.payload.clearState
            ? adapter.setAll(action.payload.items, {
                ...state,
                pagination: action.payload.pagination,
                listLoading: undefined
            })
            : adapter.upsertMany(action.payload.items, {
                ...state,
                pagination: action.payload.pagination,
                listLoading: undefined
            });
    }),

    on(AssetDocumentsActions.LoadListFailure, (state) => ({
        ...state,
        listLoading: undefined
    })),

    on(AssetDocumentsActions.SetFilter, (state, action) => ({
        ...state,
        filter: action.payload
    })),

    on(AssetDocumentsActions.SetPagination, (state, action) => ({
        ...state,
        pagination: {...state.pagination, number: action.payload ? StoredComponentHelperService.setNextPage(state.pagination, state.ids?.length) : 0}
    })),

    on(AssetDocumentsActions.SetLoading, (state, action) => ({
        ...state,
        listLoading: action.payload
    })),

    on(AssetDocumentsActions.SetItemsLoading, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id, action.payload.isStart)
    })),

    on(AssetDocumentsActions.UpdateItemSuccess, (state, action) => {
        const savedItemAvailableByFilter = isAvailableByFilter(state.filter, action.payload);
        if (savedItemAvailableByFilter) {
            return adapter.setOne(action.payload!, {
                ...state,
                itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
                pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids)}
            });
        }

        return adapter.removeOne(action.payload.id!, {
            ...state,
            itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
            pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids, true)}
        });
    }),

    on(AssetDocumentsActions.DeleteItem, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, true)
    })),

    on(AssetDocumentsActions.DeleteItemSuccess, (state, action) => {
        return adapter.removeOne(action.payload.id!, {
            ...state,
            itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
            pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids, true)}
        });
    }),

    on(AssetDocumentsActions.DeleteItemFailure, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false)
    })),

    on(AssetDocumentsActions.SetInitialState, SetAllInitialStates, () => ({...initialState}))
);

export const {selectAll: selectAllEntities} = adapter.getSelectors();

const isAvailableByFilter = (searchParams: AssetDocumentSearchParams | undefined, item: AssetDocument): boolean => {
    if (!searchParams) {
        return true;
    }

    if ((searchParams.operative || searchParams.operative === false) && item.operative !== searchParams.operative) {
        return false;
    }

    if (searchParams.from && dayjs(searchParams.from).isAfter(dayjs(item.date))) {
        return false;
    }

    if (searchParams.to && dayjs(item.date).isAfter(dayjs(searchParams.to))) {
        return false;
    }

    if (searchParams.nomenclature && item.nomenclature?.id != searchParams.nomenclature.id) {
        return false;
    }

    return !(!item.comment && searchParams.query || searchParams.query && (item.comment as string).toLowerCase().indexOf(searchParams.query.toLowerCase()) == -1);
};
